// Fingerprint / face sign-in for the phone app.
//
// After you sign in once with your email and password, the app can keep them in the phone's secure
// vault, locked behind your fingerprint or face. Next time you are signed out, the welcome screen
// asks for your fingerprint instead of the email and password.
//
// Like utils/push.js this does NOT import any @capacitor package: the plugin
// (@capgo/capacitor-native-biometric) is provided by the phone app at runtime, so the website
// build never depends on it. If the plugin is not in the app build, everything here quietly says
// "not available" and the app behaves exactly as before.

import { isNative } from './native';

const SERVER = 'app.carcollectorstudio.com';
const BIOMETRY_ANY = 2;   // the vault survives adding another fingerprint

const plugin = () => {
  if (!isNative || typeof window === 'undefined') return null;
  const cap = window.Capacitor;
  return cap?.Plugins?.NativeBiometric || (typeof cap?.registerPlugin === 'function' ? cap.registerPlugin('NativeBiometric') : null);
};

// Does this phone have a fingerprint / face set up that the app may use?
export const biometricAvailable = async () => {
  const p = plugin();
  if (!p) return false;
  try { return !!(await p.isAvailable({ useFallback: false }))?.isAvailable; } catch { return false; }
};

// Has this person already turned it on (their sign-in is in the vault)?
export const biometricSaved = async () => {
  const p = plugin();
  if (!p) return false;
  try { return !!(await p.isCredentialsSaved({ server: SERVER }))?.isSaved; } catch { return false; }
};

// Available on this phone AND already turned on.
export const biometricReady = async () => (await biometricAvailable()) && (await biometricSaved());

// Turn it on: puts the email and password in the vault (the phone asks for a fingerprint to confirm).
export const saveBiometric = async (email, password) => {
  const p = plugin();
  if (!p) throw new Error('Biometric sign-in is not available on this phone');
  await p.setCredentials({
    username: email, password, server: SERVER, accessControl: BIOMETRY_ANY,
    title: 'Turn on biometric sign-in', negativeButtonText: 'Cancel',
  });
};

// Ask for the fingerprint / face and hand back { username, password }. Rejects if cancelled.
export const unlockBiometric = async () => {
  const p = plugin();
  if (!p) throw new Error('Biometric sign-in is not available on this phone');
  return p.getSecureCredentials({
    server: SERVER, title: 'Sign in', subtitle: 'Car Collector Studio', negativeButtonText: 'Use password',
  });
};

// Turn it off: removes the email and password from the vault.
export const forgetBiometric = async () => {
  const p = plugin();
  if (!p) return;
  try { await p.deleteCredentials({ server: SERVER }); } catch { /* nothing was saved */ }
};
